import { __decorate } from "tslib";
import { Model, modelRegistry } from './Model';
import { ClientIDType } from '../types';
import { I18n, LocaleOverride } from '../wrappers/I18n';
import { Printable } from '../print';
import { dateFormatter } from '../utilities/dateFormatter';
import { apiService, connectionService } from '../services';
import Money from '../utilities/Money';
import moment from 'moment-timezone';
const { formatDateTimeHuman } = dateFormatter();
export class Client extends Model {
    static entity = 'clients';
    id = null;
    name = '';
    cost_center = null;
    address = null;
    city = null;
    postal_number = null;
    country = null;
    card = null;
    comment = null;
    discount_percent = '0.00';
    phone = null;
    email = null;
    tax_id_number = null;
    registration_number = null;
    vat_number = null;
    id_type = ClientIDType.tax_id_number;
    id_number = '';
    tax_id_card_number = null;
    debt = '0.0000';
    pricelist_id = null;
    settings = {};
    created_at = null;
    updated_at = null;
    static fields() {
        return {
            id: this.attr(null),
            name: this.attr(null),
            cost_center: this.attr(null),
            address: this.attr(null),
            city: this.attr(null),
            postal_number: this.attr(null),
            country: this.attr(null),
            card: this.attr(null),
            comment: this.attr(null),
            discount_percent: this.attr('0.00', value => this.round(value, 2).toFixed(2)),
            phone: this.attr(null),
            email: this.attr(null),
            tax_id_number: this.attr(null),
            registration_number: this.attr(null),
            vat_number: this.attr(null),
            id_type: this.attr(ClientIDType.tax_id_number),
            id_number: this.attr(''),
            tax_id_card_number: this.attr(null),
            debt: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            pricelist_id: this.attr(null),
            settings: this.attr({}, value => value || {}),
            created_at: this.attr(null),
            updated_at: this.attr(null),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    getDiscountPercent() {
        return parseFloat(this.discount_percent);
    }
    getDebt() {
        return Money.parse(this.debt);
    }
    async addInstallment(value) {
        if (!connectionService.isOnline) {
            return { success: false, error: I18n.t('connection.internet_offline_message_not_allowed') };
        }
        const response = await apiService.patch(`/clients/${this.id}/payment`, { payment: value });
        Client.update(response.data.client);
        this.debt = this.getDebt().subtract(Money.parse(value)).toFormat('0.0000');
        return { success: true };
    }
    getInstallmentPrintable(value) {
        const pr = new Printable();
        pr.l('L', `${I18n.t('general.buyer')}: ${this.name}`);
        pr.l();
        pr.l();
        pr.l('L', `${I18n.t('general.installments_paid')}: ${Money.parse(value).toFormat('0.00')}`);
        pr.l('L', `${I18n.t('general.installments_remaining')}: ${this.getDebt().toFormat('0.00')}`);
        pr.l('L', `${I18n.t('general.time')}: ${formatDateTimeHuman(moment())}`);
        pr.l();
        pr.l();
        pr.l();
        pr.l('L', I18n.t('general.cashier'));
        pr.l();
        pr.l('L', '________________________________');
        pr.l();
        pr.l('L', I18n.t('general.buyer'));
        pr.l();
        pr.l('L', '________________________________');
        return pr;
    }
    isDiplomat() {
        return false;
    }
    static idTypes() {
        return [
            { id: ClientIDType.personal_id_number, text: I18n.t('clients.id_types.personal_id_number'), prefix: null },
            { id: ClientIDType.passport_number, text: I18n.t('clients.id_types.passport_number'), prefix: null },
            { id: ClientIDType.vat_number, text: I18n.t('clients.id_types.vat_number'), prefix: null },
            { id: ClientIDType.tax_number, text: I18n.t('clients.id_types.tax_number'), prefix: null },
            { id: ClientIDType.social_security_number, text: I18n.t('clients.id_types.social_security_number'), prefix: null },
        ];
    }
    getFullIdNumber() {
        return this.id_number || this.tax_id_number || '';
    }
    getTaxIdNumber() {
        if (this.id_type === ClientIDType.tax_id_number)
            return this.id_number;
        return this.tax_id_number || '';
    }
    einvoiceAllowed() {
        return false;
    }
    isLocalCountry() {
        return true;
    }
    isValid() {
        return true;
    }
    /**
     * Make a copy of the Client.
     */
    $duplicate() {
        return new Client(this.$toJson());
    }
    matches(query) {
        const queryText = query.toLowerCase();
        return !!(this.name && this.name.toLowerCase().indexOf(queryText) > -1)
            || !!(this.tax_id_number && this.tax_id_number.toLowerCase().indexOf(queryText) > -1)
            || !!(this.id_number && this.id_number.toLowerCase().indexOf(queryText) > -1)
            || !!(this.registration_number && this.registration_number.toLowerCase().indexOf(queryText) > -1)
            || !!(this.card && this.card.toLowerCase().indexOf(queryText) > -1);
    }
    static search(query) {
        return Client.all().filter(client => client.matches(query));
    }
    /**
     * First client whose name contains the platform source token
     * (case-insensitive). `source` is a fixed registry token like
     * 'glovo' / 'wolt'; null is returned when nothing matches.
     */
    static findByPlatformSource(source) {
        if (!source)
            return null;
        const needle = source.toLowerCase();
        return this.all().find(c => (c.name || '').toLowerCase().includes(needle)) ?? null;
    }
    static async detect(idNumber, countryCode = I18n.c('general.country')) {
        try {
            const response = await apiService.get(`/clients/search/${idNumber}`, {
                params: { countryCode }
            });
            if (!response.data.success)
                return undefined;
            return {
                name: response.data.company?.name,
                address: response.data.company?.address,
                city: response.data.company?.city,
                registration_number: response.data.company?.registration_number,
                bank_account_number: response.data.company?.bank_account_number,
                phone_number: response.data.company?.phone_number,
                email_address: response.data.company?.email_address,
            };
        }
        catch (error) {
            return undefined;
        }
    }
}
__decorate([
    LocaleOverride
], Client.prototype, "isDiplomat", null);
__decorate([
    LocaleOverride
], Client.prototype, "getFullIdNumber", null);
__decorate([
    LocaleOverride
], Client.prototype, "getTaxIdNumber", null);
__decorate([
    LocaleOverride
], Client.prototype, "einvoiceAllowed", null);
__decorate([
    LocaleOverride
], Client.prototype, "isLocalCountry", null);
__decorate([
    LocaleOverride
], Client.prototype, "isValid", null);
__decorate([
    LocaleOverride
], Client, "idTypes", null);
export default Client;
modelRegistry.register('Client', Client);
//# sourceMappingURL=Client.js.map